import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const API_BASE = 'https://chiriri-keiba.com';
const JYO_MAP = {
  '01': '札幌',
  '02': '函館',
  '03': '福島',
  '04': '新潟',
  '05': '東京',
  '06': '中山',
  '07': '中京',
  '08': '京都',
  '09': '阪神',
  '10': '小倉',
};

const LIMIT_UMAREN = 30;
const LIMIT_SANRENPUKU = 30;
const LIMIT_SANRENTAN = 50;
const PAGE_KEY = 'chiriri_model_kaime_test';

const formatRaceLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const s = raw.replace('Ｒ', 'R');
  const m = s.match(/^([^\d]+)\s*(\d+)\s*R?$/);
  if (m) return `${m[1].trim()}${Number(m[2])}R`;

  const digits = s.replace(/\D/g, '');
  if (digits.length >= 10) {
    const jyo = JYO_MAP[digits.slice(8, 10)];
    const raceNo = Number(digits.slice(-2));
    if (jyo && Number.isFinite(raceNo) && raceNo > 0) return `${jyo}${raceNo}R`;
  }
  return raw;
};

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const formatProbPct = (v) => {
  const n = toNum(v);
  if (n === null) return '-';
  // `prob` は 0-1、`prob_pct` は 0-100 を想定
  if (n <= 1) return `${(n * 100).toFixed(2)}%`;
  return `${n.toFixed(2)}%`;
};

const formatYen = (v) => {
  const n = toNum(v);
  if (n === null) return '-';
  return `${Math.round(n).toLocaleString()}円`;
};

const formatOdds = (v) => {
  const n = toNum(v);
  if (n === null) return '-';
  return n.toFixed(1);
};

const formatNumber = (value, digits = 1) => {
  const n = toNum(value);
  if (n === null) return '-';
  return n.toFixed(digits);
};

const formatEv = (v) => {
  const n = toNum(v);
  if (n === null) return '-';
  return n.toFixed(3);
};

const formatBodyWeight = (weight, diff) => {
  const w = toNum(weight);
  if (w === null) return '-';
  const body = `${Math.round(w)}`;
  const d = toNum(diff);
  if (d === null) return body;
  if (d > 0) return `${body}(+${Math.round(d)})`;
  if (d < 0) return `${body}(${Math.round(d)})`;
  return `${body}(0)`;
};

const formatFinishPosition = (value) => {
  if (value === undefined || value === null || value === '') return '-';
  const n = toNum(value);
  if (n !== null) return n > 0 ? `${Math.round(n)}` : '-';
  return String(value);
};

const sumStakeYen = (rows) =>
  (rows || []).reduce((acc, row) => {
    const stake = toNum(row?.stake_yen);
    return acc + (stake ?? 0);
  }, 0);

const toComboLabel = (row, withOrder = false) => {
  const a = row?.umaban_a ?? '-';
  const b = row?.umaban_b ?? '-';
  const c = row?.umaban_c ?? '-';
  if (row?.umaban_c !== undefined && row?.umaban_c !== null && row?.umaban_c !== '') {
    return withOrder ? `${a}→${b}→${c}` : `${a}-${b}-${c}`;
  }
  return `${a}-${b}`;
};

const buildTanpukuRows = (list) => {
  const rows = (list || []).map((r) => ({
    umaban: r.umaban,
    jockey_name: r.jockey_name ?? '-',
    futan_juryo: r.futan_juryo,
    bataiju: r.bataiju,
    zogen_sa: r.zogen_sa,
    finish_position: r.finish_position ?? r.kakutei_chakujun ?? r.rank,
    running_style: r.running_style ?? '-',
    odds: r.tansho_odds,
    base_odds: r.base_odds,
    p: r.p,
    p_rank_in_race: r.p_rank_in_race ?? r.p_rank,
    pick: Number(r.pick) === 1,
    edge: r.edge,
    compi_index: r.compi_index ?? '-',
  }));
  return rows.sort((a, b) => {
    const ra = toNum(a.p_rank_in_race);
    const rb = toNum(b.p_rank_in_race);
    if (ra !== null && rb !== null) return ra - rb;
    return (toNum(b.p) ?? 0) - (toNum(a.p) ?? 0);
  });
};

function ChiririModelKaimePage() {
  const theme = useTheme();
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [modelInfo, setModelInfo] = useState(null);
  const [modelInfoError, setModelInfoError] = useState('');
  const [raceList, setRaceList] = useState([]);
  const [selectedRace, setSelectedRace] = useState('');
  const [selectedRaceLabel, setSelectedRaceLabel] = useState('');
  const [data, setData] = useState({
    tanpuku_df: [],
    umaren_df: [],
    sanrenpuku_df: [],
    sanrentan_df: [],
    race_name: '',
  });
  const [loading, setLoading] = useState(true);
  const [raceLoading, setRaceLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const fetchDates = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/chiriri_model/available_dates`, {
          params: { page: PAGE_KEY },
        });
        const dates = res.data?.dates ?? [];
        setAvailableDates(dates);
        if (dates.length > 0) {
          setSelectedDate(dates[0]);
        } else {
          setLoading(false);
        }
      } catch (e) {
        setErrorMsg('日付一覧の取得に失敗しました。');
        setLoading(false);
      }
    };
    fetchDates();
  }, []);

  useEffect(() => {
    const fetchModelInfo = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/chiriri_model/current_model`, {
          params: { page: PAGE_KEY },
        });
        setModelInfo(res.data ?? null);
        setModelInfoError('');
      } catch (e) {
        setModelInfo(null);
        setModelInfoError('currentモデル情報の取得に失敗しました。');
      }
    };
    fetchModelInfo();
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    const fetchRaceList = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/api/chiriri_model/race_list`, {
          params: { date: selectedDate, page: PAGE_KEY },
        });
        setRaceList(res.data?.race_list ?? []);
        setSelectedRace('');
        setSelectedRaceLabel('');
        setData({
          tanpuku_df: [],
          umaren_df: [],
          sanrenpuku_df: [],
          sanrentan_df: [],
          race_name: '',
        });
        setErrorMsg('');
      } catch (e) {
        setErrorMsg('レース一覧の取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };
    fetchRaceList();
  }, [selectedDate]);

  const fetchRaceData = async (race) => {
    if (typeof race !== 'string' && race?.has_prediction === false) {
      setErrorMsg('このレースは表示対象ですが、予測ファイルが未生成のためまだ開けません。');
      return;
    }
    const raceKey = typeof race === 'string' ? race : race?.race_key || race?.race_name;
    const raceName = typeof race === 'string' ? race : race?.race_name || race?.race_key;
    setSelectedRace(raceKey || raceName || '');
    setSelectedRaceLabel(formatRaceLabel(raceName || raceKey));
    setRaceLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get(`${API_BASE}/api/chiriri_model/predictions`, {
        params: { race_info: raceKey || raceName, date: selectedDate, page: PAGE_KEY },
      });
      setData({
        tanpuku_df: res.data?.tanpuku_df ?? [],
        umaren_df: res.data?.umaren_df ?? [],
        sanrenpuku_df: res.data?.sanrenpuku_df ?? [],
        sanrentan_df: res.data?.sanrentan_df ?? [],
        race_name: res.data?.race_name ?? '',
      });
      setSelectedRaceLabel((prev) => prev || formatRaceLabel(res.data?.race_name || raceName || raceKey));
    } catch (e) {
      setErrorMsg('買目データの取得に失敗しました。');
      setData({
        tanpuku_df: [],
        umaren_df: [],
        sanrenpuku_df: [],
        sanrentan_df: [],
        race_name: '',
      });
    } finally {
      setRaceLoading(false);
    }
  };

  const umarenRows = useMemo(() => (data.umaren_df || []).slice(0, LIMIT_UMAREN), [data.umaren_df]);
  const sanrenpukuRows = useMemo(
    () => (data.sanrenpuku_df || []).slice(0, LIMIT_SANRENPUKU),
    [data.sanrenpuku_df]
  );
  const sanrentanRows = useMemo(
    () => (data.sanrentan_df || []).slice(0, LIMIT_SANRENTAN),
    [data.sanrentan_df]
  );
  const missingPredictionCount = useMemo(
    () => raceList.filter((race) => typeof race !== 'string' && race?.has_prediction === false).length,
    [raceList]
  );
  const tanpukuRows = useMemo(() => buildTanpukuRows(data.tanpuku_df), [data.tanpuku_df]);
  const honmeiLabels = useMemo(
    () => tanpukuRows.filter((row) => row.pick).map((row) => `${row.umaban}番`),
    [tanpukuRows]
  );

  const displayRaceName = selectedRaceLabel || formatRaceLabel(data?.race_name);

  if (loading) return <div>読み込み中...</div>;

  return (
    <Box p={{ xs: 0, sm: 4 }} sx={{ width: '100%' }}>
      <Alert severity="warning" sx={{ mb: 2, fontWeight: 'bold' }}>
        ※検証用ページ: Harville式で馬連/三連複/三連単を生成しています
      </Alert>
      <Alert severity="info" sx={{ mb: 3 }}>
        オッズ取得済みの買目に対して、期待値と推奨配分（Kelly比率ベース）を表示します。
      </Alert>
      {modelInfo && (
        <Alert severity="success" sx={{ mb: 3 }}>
          chiriri_model currentモデル: {modelInfo.model_label || '-'} / S3: {modelInfo.model_s3_prefix || '-'}
        </Alert>
      )}
      {modelInfoError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {modelInfoError}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel id="date-select-label">日付選択</InputLabel>
          <Select
            labelId="date-select-label"
            value={selectedDate}
            label="日付選択"
            onChange={(e) => setSelectedDate(e.target.value)}
          >
            {availableDates.map((date) => (
              <MenuItem key={date} value={date}>
                {date}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {missingPredictionCount > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          表示対象レースのうち {missingPredictionCount} レースは予測未生成のため、ボタンを無効化しています。
        </Alert>
      )}

      <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, mb: 1 }}>
        レース選択
      </Typography>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
        {raceList.map((race) => {
          const raceName = typeof race === 'string' ? race : race?.race_name;
          const raceKey = typeof race === 'string' ? race : race?.race_key || race?.race_name;
          const hasPrediction = typeof race === 'string' ? true : race?.has_prediction !== false;
          const predictionStatusLabel =
            typeof race === 'string' ? '' : race?.prediction_status_label || '予測未生成';
          if (!raceName) return null;
          return (
            <Button
              key={raceKey || raceName}
              variant="contained"
              color="primary"
              disabled={!hasPrediction}
              title={!hasPrediction ? predictionStatusLabel : ''}
              onClick={() => fetchRaceData({ race_key: raceKey, race_name: raceName })}
              sx={{
                mr: 1,
                mb: 1,
                backgroundColor: selectedRace === (raceKey || raceName) ? theme.palette.secondary.main : undefined,
                fontWeight: 'bold',
              }}
            >
              {raceName}
            </Button>
          );
        })}
      </Typography>

      {errorMsg && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMsg}</Alert>
        </Box>
      )}

      {raceLoading && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info" icon={<CircularProgress size={16} />}>
            買目データを読み込み中...
          </Alert>
        </Box>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
          買目生成 {displayRaceName ? `（${displayRaceName}）` : ''}
        </Typography>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 1 }}>
          単勝予測一覧
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          総頭数: {tanpukuRows.length} / ◎: {honmeiLabels.length ? honmeiLabels.join('、') : '該当なし'}
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">馬番</TableCell>
                <TableCell align="center">騎手</TableCell>
                <TableCell align="center">斤量</TableCell>
                <TableCell align="center">馬体重</TableCell>
                <TableCell align="center">脚質</TableCell>
                <TableCell align="center">着順</TableCell>
                <TableCell align="center">単勝オッズ</TableCell>
                <TableCell align="center">基準オッズ</TableCell>
                <TableCell align="center">予測勝率</TableCell>
                <TableCell align="center">推奨</TableCell>
                <TableCell align="center">期待値</TableCell>
                <TableCell align="center">能力指数</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tanpukuRows.map((row, idx) => (
                <TableRow key={`tanpuku-${row.umaban ?? idx}`}>
                  <TableCell align="center">{row.umaban ? `${row.umaban}番` : '-'}</TableCell>
                  <TableCell align="center">{row.jockey_name || '-'}</TableCell>
                  <TableCell align="center">{formatNumber(row.futan_juryo, 1)}</TableCell>
                  <TableCell align="center">{formatBodyWeight(row.bataiju, row.zogen_sa)}</TableCell>
                  <TableCell align="center">{row.running_style || '-'}</TableCell>
                  <TableCell align="center">{formatFinishPosition(row.finish_position)}</TableCell>
                  <TableCell align="center">{formatOdds(row.odds)}</TableCell>
                  <TableCell align="center">{formatOdds(row.base_odds)}</TableCell>
                  <TableCell align="center">{formatProbPct(row.p)}</TableCell>
                  <TableCell align="center">{row.pick ? '◎' : ''}</TableCell>
                  <TableCell align="center">{formatNumber(row.edge, 2)}</TableCell>
                  <TableCell align="center">{row.compi_index}</TableCell>
                </TableRow>
              ))}
              {!tanpukuRows.length && (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    データなし
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 1 }}>
          馬連（上位{LIMIT_UMAREN}件）
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          総件数: {data.umaren_df?.length ?? 0} / 表示件数: {umarenRows.length} / 推奨配分合計:{' '}
          {formatYen(sumStakeYen(umarenRows))}
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">順位</TableCell>
                <TableCell align="center">買目</TableCell>
                <TableCell align="center">確率</TableCell>
                <TableCell align="center">オッズ</TableCell>
                <TableCell align="center">期待値</TableCell>
                <TableCell align="center">推奨配分</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {umarenRows.map((row, idx) => (
                <TableRow key={`umaren-${idx}`}>
                  <TableCell align="center">{row.rank ?? idx + 1}</TableCell>
                  <TableCell align="center">{toComboLabel(row, false)}</TableCell>
                  <TableCell align="center">{formatProbPct(row.prob ?? row.prob_pct)}</TableCell>
                  <TableCell align="center">{formatOdds(row.odds)}</TableCell>
                  <TableCell align="center">{formatEv(row.ev)}</TableCell>
                  <TableCell align="center">{formatYen(row.stake_yen)}</TableCell>
                </TableRow>
              ))}
              {!umarenRows.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    データなし
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 1 }}>
          三連複（上位{LIMIT_SANRENPUKU}件）
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          総件数: {data.sanrenpuku_df?.length ?? 0} / 表示件数: {sanrenpukuRows.length} / 推奨配分合計:{' '}
          {formatYen(sumStakeYen(sanrenpukuRows))}
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">順位</TableCell>
                <TableCell align="center">買目</TableCell>
                <TableCell align="center">確率</TableCell>
                <TableCell align="center">オッズ</TableCell>
                <TableCell align="center">期待値</TableCell>
                <TableCell align="center">推奨配分</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sanrenpukuRows.map((row, idx) => (
                <TableRow key={`sanrenpuku-${idx}`}>
                  <TableCell align="center">{row.rank ?? idx + 1}</TableCell>
                  <TableCell align="center">{toComboLabel(row, false)}</TableCell>
                  <TableCell align="center">{formatProbPct(row.prob ?? row.prob_pct)}</TableCell>
                  <TableCell align="center">{formatOdds(row.odds)}</TableCell>
                  <TableCell align="center">{formatEv(row.ev)}</TableCell>
                  <TableCell align="center">{formatYen(row.stake_yen)}</TableCell>
                </TableRow>
              ))}
              {!sanrenpukuRows.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    データなし
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 1 }}>
          三連単（上位{LIMIT_SANRENTAN}件）
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          総件数: {data.sanrentan_df?.length ?? 0} / 表示件数: {sanrentanRows.length} / 推奨配分合計:{' '}
          {formatYen(sumStakeYen(sanrentanRows))}
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">順位</TableCell>
                <TableCell align="center">買目</TableCell>
                <TableCell align="center">確率</TableCell>
                <TableCell align="center">オッズ</TableCell>
                <TableCell align="center">期待値</TableCell>
                <TableCell align="center">推奨配分</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sanrentanRows.map((row, idx) => (
                <TableRow key={`sanrentan-${idx}`}>
                  <TableCell align="center">{row.rank ?? idx + 1}</TableCell>
                  <TableCell align="center">{toComboLabel(row, true)}</TableCell>
                  <TableCell align="center">{formatProbPct(row.prob ?? row.prob_pct)}</TableCell>
                  <TableCell align="center">{formatOdds(row.odds)}</TableCell>
                  <TableCell align="center">{formatEv(row.ev)}</TableCell>
                  <TableCell align="center">{formatYen(row.stake_yen)}</TableCell>
                </TableRow>
              ))}
              {!sanrentanRows.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    データなし
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default ChiririModelKaimePage;
